'use client';

import { useState } from 'react';

interface ExpoSnackPreviewProps {
  snackId: string;
  title: string;
  className?: string;
  height?: string;
}

const ExpoSnackPreview = ({ 
  snackId, 
  title, 
  className = '', 
  height = '400px' 
}: ExpoSnackPreviewProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const snackUrl = `https://snack.expo.dev/${snackId}`;
  const embedUrl = `https://snack.expo.dev/embedded/${snackId}`;

  return (
    <div className={`relative bg-zinc-900 rounded-lg overflow-hidden border border-zinc-700 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 bg-zinc-800 border-b border-zinc-700">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
        </div>
        <div className="text-zinc-300 text-sm font-medium">
          {title}
        </div>
        <a
          href={snackUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-400 hover:text-blue-300 text-sm transition-colors"
        >
          Open in Snack →
        </a>
      </div>

      {/* Preview Container */}
      <div className="relative" style={{ height }}>
        {!isLoaded && !hasError && (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
            <div className="flex flex-col items-center space-y-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              <div className="text-zinc-400 text-sm">Loading Expo Snack...</div>
            </div>
          </div>
        )}

        {hasError ? (
          <div className="absolute inset-0 flex items-center justify-center bg-zinc-900">
            <div className="text-center p-6">
              <div className="text-red-400 mb-2">
                <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="text-zinc-300 text-sm mb-3">
                Preview not available
              </div>
              <a
                href={snackUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Open in Snack
              </a>
            </div>
          </div>
        ) : (
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
            title={`${title} - Expo Snack Preview`}
            sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
          />
        )}
      </div>

      {/* Footer */}
      <div className="p-3 bg-zinc-800 border-t border-zinc-700">
        <div className="flex items-center justify-between text-xs text-zinc-400">
          <div className="flex items-center space-x-4">
            <span>📱 Interactive Preview</span>
            <span>⚡ Real-time</span>
          </div>
          <div className="flex items-center space-x-2">
            <span>Powered by</span>
            <span className="text-blue-400 font-semibold">Expo Snack</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpoSnackPreview;
